import { Component, OnInit, Renderer2 } from '@angular/core';

import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map, startWith } from 'rxjs/operators';

import { CountryLanguage, CountryState } from '@fcom/core';
import { shownConfs } from '@fcom/core/reducers/langs';

import { CmsPageComponentMetaData, Links, PageMetaService } from '../../services';

const parseLocalizedPathFromUrl = (langCode: string, url: string): string => {
  const everythingBeforeLangCode = new RegExp(`.+?(?=/${langCode})`);
  return url ? url.replace(everythingBeforeLangCode, '') : `/${langCode}`;
};

const LANG = 'en';
const DESCRIPTION =
  'Finnair flies the shortcut between Europe and Asia. Start by selecting your country and preferred language and then proceed to booking your flights.';
const META_TAGS: CmsPageComponentMetaData[] = [
  {
    tagName: 'meta',
    attributes: {
      name: 'description',
      content: DESCRIPTION,
    },
  },
  {
    tagName: 'meta',
    attributes: {
      name: 'og:description',
      content: DESCRIPTION,
    },
  },
  {
    tagName: 'meta',
    attributes: {
      name: 'og:site_name',
      content: 'Finnair',
    },
  },
];

@Component({
  selector: 'fin-select-language',
  styleUrls: ['./select-language.component.scss'],
  templateUrl: './select-language.component.html',
})
export class SelectLanguageComponent implements OnInit {
  static readonly TITLE = 'Flight bookings | select country and language | Finnair';

  countries: CountryState[] = shownConfs
    .map((country) => ({
      ...country,
      languages: country.languages.map((language): CountryLanguage => {
        const translation = language.translation ? ` (${language.translation})` : '';
        return {
          ...language,
          label: `${country.name}, ${language.name}${translation}`,
          ISOCode: language.code.split('-').pop(),
        };
      }),
    }))
    .reduce((countryList, country) => {
      // Set Finland as first country in the list
      if (country.code === 'FI') {
        countryList.unshift(country);
      } else {
        countryList.push(country);
      }
      return countryList;
    }, []);

  countries$: Observable<CountryState[]>;

  constructor(
    public pageMetaService: PageMetaService,
    private renderer: Renderer2
  ) {}

  ngOnInit(): void {
    this.countries$ = combineLatest([this.pageMetaService.links, this.pageMetaService.prevPageUrl$]).pipe(
      map(([links, prevPageUrl]) =>
        this.countries.map((country) => this.mapCountryLanguageLinks(country, links, prevPageUrl))
      ),
      distinctUntilChanged(),
      startWith(this.countries)
    );

    this.pageMetaService.setTitle(SelectLanguageComponent.TITLE);
    this.pageMetaService.setDocumentLanguage(LANG, this.renderer);
    this.pageMetaService.setMetaTags(META_TAGS, this.renderer);
  }

  private mapCountryLanguageLinks = (country: CountryState, links: Links, prevPageUrl: string): CountryState => ({
    ...country,
    languages: country.languages.map((language) => ({
      ...language,
      link: this.getLocalizedLinkForLangCode(language.code, links, prevPageUrl),
    })),
  });

  private getLocalizedLinkForLangCode = (langCode: string, links: Links = {}, prevPageUrl: string): string => {
    const localizedCmsPageUrl = parseLocalizedPathFromUrl(langCode, links[langCode]);
    const localizedPrevPageUrl = prevPageUrl ? `/${langCode}${prevPageUrl}` : undefined;
    return localizedPrevPageUrl ?? localizedCmsPageUrl;
  };
}
